import _ from 'lodash';
import styled from 'styled-components';
import { Box, Flex } from 'reflexbox';

import { Heading, Jumbo, Tabs } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import EngagementMetric from './metrics/engagement/EngagementMetric';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import useViewerRoles from 'packages/kakapo-ui-application/hooks/useUserRoles';
import { useTranslation } from 'react-i18next';

const Column = styled(Box)`
	flex-shrink: 0;
`;

function DashboardEngagementTrend(props: {
	trends: graphql.AnalyticsEngagementSubcategoryScoreOverTime[];
}) {
	const { localize } = useLocalize();

	const trends = _.sortBy(props.trends, (trend) => localize(trend.subcategory.title).text);

	return (
		<>
			<Heading mb={3}>{localize(props.trends[0].subcategory.category.title).text}</Heading>
			<Flex flexWrap="wrap" mb={5}>
				<Column width={[1, 1, 1 / 2]}>
					<Box mr={[0, 0, 2]}>
						{trends
							.filter((_, i) => i % 2 === 0)
							.map((trend) => (
								<Box mb={3} key={trend.subcategory.id}>
									<EngagementMetric
										data={trend as graphql.AnalyticsEngagementSubcategoryScoreOverTime}
									/>
								</Box>
							))}
					</Box>
				</Column>
				<Column width={[1, 1, 1 / 2]}>
					<Box ml={[0, 0, 2]}>
						{trends
							.filter((_, i) => i % 2 === 1)
							.map((trend) => (
								<Box mb={3} key={trend.subcategory.id}>
									<EngagementMetric
										data={trend as graphql.AnalyticsEngagementSubcategoryScoreOverTime}
									/>
								</Box>
							))}
					</Box>
				</Column>
			</Flex>
		</>
	);
}

function EngagementTopics(props: {
	data:
		| graphql.WorkspaceEngagementTopicsQuery
		| graphql.GroupEngagementTopicsQuery
		| graphql.EngagementOfficerTopicsQuery;
}) {
	const { localize } = useLocalize();
	const data =
		'workspaceAnalytics' in props.data
			? props.data.workspaceAnalytics
			: 'groupAnalytics' in props.data
			? props.data.groupAnalytics
			: props.data.engagementAnalytics;

	const subcategories = data.engagementSubcategoriesTrend;
	const categories = new Map(
		subcategories.map((trend) => [trend.subcategory.category.id, trend.subcategory.category]),
	);
	const grouped = _.groupBy(subcategories, (trend) => trend.subcategory.category.id);
	const groups = _.sortBy(Object.keys(grouped), (id) => localize(categories.get(id)!.title).text);

	return (
		<>
			{groups.map((id) => (
				<DashboardEngagementTrend
					key={id}
					trends={grouped[id] as graphql.AnalyticsEngagementSubcategoryScoreOverTime[]}
				/>
			))}
		</>
	);
}

function EngagementOfficerTopics(props: { filters: graphql.AnalyticsFilters }) {
	const query = graphql.useEngagementOfficerTopicsQuery({
		variables: {
			filters: props.filters,
		},
	});

	if (!query.data) {
		return null;
	}

	return <EngagementTopics data={query.data} />;
}

function WorkspaceTopics(props: { filters: graphql.AnalyticsFilters }) {
	const query = graphql.useWorkspaceEngagementTopicsQuery({
		variables: {
			filters: props.filters,
		},
	});

	if (!query.data) {
		return null;
	}

	return <EngagementTopics data={query.data} />;
}

function GroupTopics(props: { filters: graphql.AnalyticsFilters }) {
	const query = graphql.useGroupEngagementTopicsQuery({
		variables: {
			filters: props.filters,
		},
		skip: props.filters.groupIDs?.length === 0,
	});

	if (!query.data) {
		return null;
	}

	return <EngagementTopics data={query.data} />;
}

export default function DashboardEngagementTopics(props: {
	filters: graphql.AnalyticsFilters;
	headless?: boolean;
}) {
	const userRoles = useViewerRoles();
	const { t } = useTranslation();

	const contents = userRoles.isEngagementOfficer ? (
		<EngagementOfficerTopics filters={props.filters} />
	) : userRoles.isWorkspaceSuperuser || userRoles.isWorkspaceSupervisor ? (
		<WorkspaceTopics filters={props.filters} />
	) : (
		<GroupTopics filters={props.filters} />
	);

	if (props.headless) {
		return contents;
	}

	return (
		<Box>
			<Jumbo
				title={t('dashboard.engagement.topics.title')}
				wide
				footer={
					<Tabs.Container wide>
						<Tabs>
							<Tabs.Item title={t('dashboard.organization.title')} path="/analytics" />
							<Tabs.Item
								title={t('dashboard.engagement.title')}
								selected
								path="/analytics/engagement"
							/>
						</Tabs>
					</Tabs.Container>
				}
			/>
			<Box p={[3, 3, 4]}>{contents}</Box>
		</Box>
	);
}
